import { Component, Suspense, lazy } from "react";
import { useOwnToaster } from "./ui/toaster-registry";
import { useTranslation } from "react-i18next";

/**
 * Defers omniflow-ui until a flow is actually opened.
 *
 * omniflow-ui is 6.2MB of JS plus 200KB of CSS. pages/Flows.jsx imported both
 * statically, and legacy-routes.jsx imports Flows.jsx eagerly, so every user
 * downloaded the entire flow builder on first load — including users who never
 * open Flows at all, and users who only ever look at the flows LIST, since the
 * builder renders only when a specific flow is open.
 *
 * The stylesheet is imported inside the same dynamic chunk rather than at module
 * scope, so it loads with the builder. That matters beyond bytes: that stylesheet
 * is unlayered and declares 44 `:root` custom properties using the same names as
 * our design tokens (--background, --accent, --border, --chart-*). Our own rules
 * outrank it (`html:root` at 0,1,1 beats its `:root` at 0,1,0), so there is no
 * bug today — but keeping it out of the initial load keeps it out of the cascade
 * for every page that is not the flow builder.
 *
 * Verified safe to defer: the only custom properties omniflow-ui alone provides
 * are --builder-background(-pattern), --sidebar-background, --sidebar-progress
 * and 35 --xy-* react-flow internals. The client references none of them
 * (0 utility usages, 0 var() usages), and the --xy-* ones are consumed by
 * react-flow, which ships inside this same chunk.
 */
const Omniflow = lazy(async () => {
  // Ordered deliberately: styles first, so the builder does not paint unstyled.
  await import("omniflow-ui/dist/omniflow-ui.css");
  const mod = await import("omniflow-ui");
  return { default: mod.Omniflow };
});

const OmniflowFallback = () => {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex h-full w-full items-center justify-center text-sm text-muted-foreground"
    >
      {t("common.loading")}
    </div>
  );
};

/**
 * Code-splitting introduces a failure mode that a single bundle does not have:
 * the app loads fine, then blanks when the chunk 404s. The usual cause is a
 * deploy landing while someone has the page open, which invalidates the hashed
 * chunk filenames they were served.
 *
 * There is no ErrorBoundary anywhere else in the client, so without this a failed
 * import would unmount the whole tree with no message. Recovery is a reload
 * rather than a re-render: React.lazy caches the rejected promise, so retrying in
 * place would fail identically, and a stale-deploy page needs the new index.html
 * anyway.
 */
class ChunkErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error("Failed to load the flow builder chunk", error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return <OmniflowLoadError />;
  }
}

const OmniflowLoadError = () => {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      className="flex h-full w-full flex-col items-center justify-center gap-3 p-6 text-center"
    >
      <p className="text-sm text-muted-foreground">
        {t("flows.builderLoadFailed")}
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground"
      >
        {t("common.reload")}
      </button>
    </div>
  );
};

const OmniflowLazy = (props) => {
  // omniflow-ui renders its own sonner <Toaster />. sonner is externalised from
  // its bundle, so both packages share one module and one observer — two mounted
  // Toasters render every toast twice. This tells the app-level Toaster to stand
  // down while the builder is on screen.
  useOwnToaster();

  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<OmniflowFallback />}>
        <Omniflow {...props} />
      </Suspense>
    </ChunkErrorBoundary>
  );
};

export default OmniflowLazy;
